import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchAdminMaterial,
  fetchAdminMaterials,
} from '@/api/(admin)/materials';
import type { AdminMaterial } from '@/api/(admin)/materials/types';
import type { MaterialsQueryInput } from '../-types';
import { buildMaterialsQs, toMaterialsParams } from '../-utils/listParams';

/** Prefix for every materials cache entry. */
export const adminMaterialsQueryKey = ['admin', 'materials'] as const;

/** Shared by the route loader and the page, so the prefetch warms what the page reads. */
export function adminMaterialsQueryOptions(input: MaterialsQueryInput) {
  const params = toMaterialsParams(input);
  return queryOptions({
    queryKey: [...adminMaterialsQueryKey, 'list', params] as const,
    queryFn: () => fetchAdminMaterials(buildMaterialsQs(params)),
  });
}

export function useAdminMaterials(input: MaterialsQueryInput) {
  const query = useQuery(adminMaterialsQueryOptions(input));
  const rows: AdminMaterial[] = query.data?.data ?? [];
  return {
    ...query,
    rows,
    totalCount: query.data?.metadata.pagination.total ?? 0,
  };
}

/**
 * One material by id.
 *
 * The sheet needs this and cannot use the row it was opened from: the list
 * (`/api/admin/materials`) does not return `translations`, only
 * `/api/admin/materials/:id` does. Seeding the form from a list row therefore
 * shows every language tab blank whatever is stored — and because the PUT
 * merges translations one language at a time
 * (`{...existing, ...params.translations}`), saving a name into a blank tab
 * replaces that whole language and drops its stored description.
 */
export function adminMaterialQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...adminMaterialsQueryKey, 'detail', id] as const,
    queryFn: async () => (await fetchAdminMaterial(id)).data,
  });
}

export function useAdminMaterial(id: string | null) {
  return useQuery({
    ...adminMaterialQueryOptions(id ?? ''),
    enabled: id !== null,
  });
}
